import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import api from "../../../../../api/axios";
import { companyID } from "../../../../../auth";
import { getDepartmentById } from "../../../../../services/departmentService";
import { getWorkLocationTypes, getEmployeesRole } from "../../../../../services/EmployeeDetailServices";

const HireCandidateModal = ({
  show,
  onClose,
  candidate = null,
  onHire,
  submitting = false,
}) => {
  const { t } = useTranslation();
  const companyId = companyID();

  const [formData, setFormData] = useState({
    // Basic Info (from candidate)
    first_name: "",
    last_name: "",
    middle_name: "",
    personal_email: "",
    official_email: "",
    number: "",
    alternate_number: "",
    gender: "MALE",
    date_of_birth: "",
    guardian_name: "",
    nationality: "Indian",
    marital_status: "single",
    work_experience: "",
    
    // Employment Details
    employment_id: "",
    department_id: "",
    designation_id: "",
    joining_date: "",
    employment_type: "full-time",
    employment_status: "probationary",
    probation_end_date: "",
    salary: "",
    hourly_rate: "",
    role_id: "",
    reporter_department_id: "",
    reporter_id: "",
    work_mode: "office",
    working_shift: "09:00:00 - 18:00:00"
  });

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [workingShifts, setWorkingShifts] = useState([]);
  const [reporterDepartments, setReporterDepartments] = useState([]);
  const [errors, setErrors] = useState({});
  const [salaryType, setSalaryType] = useState("monthly");
  const [showSalary, setShowSalary] = useState(false);
  const [workLocationTypes, setWorkLocationTypes] = useState([]);
  const [selectedWorkLocation, setSelectedWorkLocation] = useState("");
  const [hybridDays, setHybridDays] = useState({});
  // hybridDays: { monday: 2 (WFH) | 1 (Onsite), ... }
  // work_location_id: 2 = WFH, 1 = Onsite

  const WEEKDAYS = [
    { label: 'Mon', value: 'monday' },
    { label: 'Tue', value: 'tuesday' },
    { label: 'Wed', value: 'wednesday' },
    { label: 'Thu', value: 'thursday' },
    { label: 'Fri', value: 'friday' },
    { label: 'Sat', value: 'saturday' },
    { label: 'Sun', value: 'sunday' },
  ];

  const isHybridSelected = (selectedId) => {
    if (!selectedId) return false;
    const found = workLocationTypes.find((t) => String(t.id) === String(selectedId));
    return found && String(found.name || '').trim().toLowerCase() === 'hybrid';
  };

  // work_location_id: 2 = WFH, 1 = Onsite
  const handleHybridDayType = (day, work_location_id) => {
    setHybridDays((prev) => {
      if (prev[day] === work_location_id) {
        const next = { ...prev };
        delete next[day];
        return next;
      }
      return { ...prev, [day]: work_location_id };
    });
  };

  const getHybridDaysPayload = () =>
    Object.entries(hybridDays).map(([day_name, work_location_id]) => ({ day_name, work_location_id }));

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      if (show && companyId) {
        try {
          const response = await getDepartmentById(companyId);
          if (response?.result) {
            setDepartments(response.result);
          }
        } catch (error) {
          console.error("Error fetching departments:", error);
        }
      }
    };
    fetchDepartments();
  }, [show, companyId]);

  // Fetch designations when department is selected
  useEffect(() => {
    const fetchDesignations = async () => {
      if (formData.department_id) {
        try {
          const response = await api.get(`department/designation/${formData.department_id}`);
          if (response?.data?.success && response.data.result) {
            setDesignations(response.data.result);
          }
        } catch (error) {
          console.error("Error fetching designations:", error);
        }
      } else {
        setDesignations([]);
      }
    };
    fetchDesignations();
  }, [formData.department_id]);

  // Fetch employees for reporter selection
  useEffect(() => {
    const fetchEmployees = async () => {
      if (show && companyId) {
        try {
          const response = await api.get(`employees/employeeDetails/${companyId}`);
          if (Array.isArray(response?.data)) {
            setEmployees(response.data);
          }
        } catch (error) {
          console.error("Error fetching employees:", error);
        }
      }
    };
    fetchEmployees();
  }, [show, companyId]);

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      if (show && companyId) {
        try {
          const roles = await getEmployeesRole();
          if (Array.isArray(roles?.data)) {
            setRoles(roles.data);
          }
        } catch (error) {
          console.error("Error fetching roles:", error);
        }
      }
    };
    fetchRoles();
  }, [show, companyId]);

  // Fetch work location types
  useEffect(() => {
    const fetchWorkLocationTypes = async () => {
      if (show) {
        try {
          const workLocationTypes = await getWorkLocationTypes();
          setWorkLocationTypes(workLocationTypes?.data || []);
        } catch (error) {
          console.error("Error fetching work location types:", error);
        }
      }
    };
    fetchWorkLocationTypes();
  }, [show]);

  // Fetch working shifts
  useEffect(() => {
    const fetchWorkingShifts = async () => {
      if (show && companyId) {
        try {
          const shifts = [
            { value: "09:00:00 - 18:00:00", label: "09:00:00 - 18:00:00" },
            { value: "10:00:00 - 19:00:00", label: "10:00:00 - 19:00:00" },
            { value: "18:00:00 - 03:00:00", label: "18:00:00 - 03:00:00" },
          ];
          setWorkingShifts(shifts);
        } catch (error) {
          console.error("Error setting working shifts:", error);
        }
      }
    };
    fetchWorkingShifts();
  }, [show, companyId]);

  // Fetch reporter departments (same as departments for now)
  useEffect(() => {
    setReporterDepartments(departments);
  }, [departments]);

  // Populate form data from candidate
  useEffect(() => {
    if (show && candidate) {
      // Split full_name
      const nameParts = (candidate.full_name || "").split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
      const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";

      // Set default probation end date (90 days from joining date)
      const today = new Date();
      const joiningDate = today.toISOString().split('T')[0];
      const probationEndDate = new Date(today);
      probationEndDate.setDate(probationEndDate.getDate() + 90);

      setFormData({
        // Basic Info from candidate
        first_name: firstName,
        last_name: lastName,
        middle_name: middleName,
        personal_email: candidate.email || "",
        official_email: candidate.email || "", // Can be changed
        number: candidate.phone || "",
        alternate_number: candidate.alternate_phone || "",
        gender: candidate.gender || "MALE",
        date_of_birth: candidate.dob ? new Date(candidate.dob).toISOString().split('T')[0] : "",
        guardian_name: candidate.guardian_name || "",
        nationality: "Indian",
        marital_status: "single",
        work_experience: candidate.experience_years || "",
        
        // Employment Details - need to be filled
        employment_id: "",
        department_id: candidate.department_id || "",
        designation_id: candidate.designation_id || "",
        joining_date: joiningDate,
        employment_type: "full-time",
        employment_status: "probationary",
        probation_end_date: probationEndDate.toISOString().split('T')[0],
        salary: candidate.expected_ctc || "",
        hourly_rate: "",
        role_id: "",
        reporter_department_id: "",
        reporter_id: "",
        work_mode: "office",
        working_shift: "09:00:00 - 18:00:00"
      });

      // Generate employment ID after setting form data
      setTimeout(() => {
        if (candidate.department_id && departments.length > 0) {
          generateEmploymentId(candidate.department_id, joiningDate);
        }
      }, 200);

      setErrors({});
    }
  }, [show, candidate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: "",
      }));
    }

    // Reset designation when department changes
    if (name === "department_id") {
      setFormData(prev => ({
        ...prev,
        designation_id: "",
      }));
    }

    // Auto-generate employment ID when department or joining date changes
    if ((name === "department_id" || name === "joining_date") && departments.length > 0) {
      setTimeout(() => {
        generateEmploymentId(name === "department_id" ? newValue : formData.department_id, 
                           name === "joining_date" ? newValue : formData.joining_date);
      }, 100);
    }
  };

  const generateEmploymentId = (departmentId = null, joiningDate = null) => {
    try {
      const deptId = departmentId || formData.department_id;
      const joinDate = joiningDate || formData.joining_date;
      
      if (deptId && joinDate && departments.length > 0) {
        const dept = departments.find(d => d.id == deptId);
        const deptCode = dept?.department_name?.substring(0, 2).toUpperCase() || 
                        dept?.department?.substring(0, 2).toUpperCase() || "EMP";
        const dateCode = new Date(joinDate).getFullYear();
        const randomNum = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
        const generatedId = `${deptCode}${dateCode}${randomNum}`;
        
        setFormData(prev => ({
          ...prev,
          employment_id: generatedId
        }));
      }
    } catch (error) {
      console.error("Error generating employment ID:", error);
      // Fallback generation
      const randomId = `EMP${new Date().getFullYear()}${Math.floor(Math.random() * 9999).toString().padStart(4, '0')}`;
      setFormData(prev => ({
        ...prev,
        employment_id: randomId
      }));
    }
  };

  const handleSalaryTypeChange = (type) => {
    const currentSalary = parseFloat(formData?.salary) || 0;
    let convertedSalary = currentSalary;

    if (salaryType === "monthly" && type === "annually") {
      // Convert monthly to annually
      convertedSalary = currentSalary * 12;
    } else if (salaryType === "annually" && type === "monthly") {
      // Convert annually to monthly
      convertedSalary = currentSalary / 12;
    }

    // Round off to remove decimals
    convertedSalary = Math.round(convertedSalary);

    setSalaryType(type);
    setFormData({
      ...formData,
      salary: convertedSalary.toString(),
    });
  };



  const validateForm = () => {
    const newErrors = {};

    // Basic Info validation
    if (!formData.first_name.trim()) newErrors.first_name = t("First name is required");
    if (!formData.last_name.trim()) newErrors.last_name = t("Last name is required");
    if (!formData.personal_email.trim()) newErrors.personal_email = t("Personal email is required");
    if (!formData.official_email.trim()) newErrors.official_email = t("Official email is required");
    if (!formData.number.trim()) newErrors.number = t("Phone number is required");

    if (!formData.work_experience.trim()) newErrors.work_experience = t("Work experience is required");

    // Employment Details validation
    if (!formData.employment_id.trim()) {
      newErrors.employment_id = t("Employment ID is required");
    }
    if (!formData.department_id) newErrors.department_id = t("Department is required");
    if (!formData.designation_id) newErrors.designation_id = t("Designation is required");
    if (!formData.joining_date) newErrors.joining_date = t("Joining date is required");
    if (!formData.salary) newErrors.salary = t("Salary is required");
    if (formData.employment_type === "hourly" && !(Number(formData.hourly_rate) > 0)) {
      newErrors.hourly_rate = t("Hourly rate is required");
    }
    if (!formData.role_id) newErrors.role_id = t("Role is required");
    if (!formData.reporter_department_id) newErrors.reporter_department_id = t("Reporter department is required");
    if (!formData.reporter_id) newErrors.reporter_id = t("Reporter is required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onHire({
      ...formData,
      ...(isHybridSelected(selectedWorkLocation) ? { hybrid_days: getHybridDaysPayload() } : {}),
    });
  };

  const handleClose = () => {
    setFormData({
      first_name: "",
      last_name: "",
      middle_name: "",
      personal_email: "",
      official_email: "",
      number: "",
      alternate_number: "",
      gender: "MALE",
      date_of_birth: "",
      guardian_name: "",
      nationality: "Indian",
      marital_status: "single",
      work_experience: "",
      employment_id: "",
      department_id: "",
      designation_id: "",
      joining_date: "",
      employment_type: "full-time",
      employment_status: "probationary",
      probation_end_date: "",
      salary: "",
      hourly_rate: "",
      role_id: "",
      reporter_department_id: "",
      reporter_id: "",
      work_mode: "office",
      working_shift: "09:00:00 - 18:00:00"
    });
    setErrors({});
    setSalaryType("monthly");
    setShowSalary(false);
    setSelectedWorkLocation("");
    setHybridDays({});
    onClose();
  };

  const selectOption = (e, setState) => {
    setState(e.target.value);
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-user-plus me-2"></i>
          {t("Hire Candidate")} - {candidate?.full_name}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-4">
            <h5 className="text-primary">
              <i className="fa fa-user me-2"></i>
              {t("Basic Information")}
            </h5>
            <hr />
          </div>
          
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("First Name")} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  isInvalid={!!errors.first_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.first_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Middle Name")}</Form.Label>
                <Form.Control
                  type="text"
                  name="middle_name"
                  value={formData.middle_name}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Last Name")} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  isInvalid={!!errors.last_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.last_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Personal Email")} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  name="personal_email"
                  value={formData.personal_email}
                  onChange={handleInputChange}
                  isInvalid={!!errors.personal_email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.personal_email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Official Email")} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  name="official_email"
                  value={formData.official_email}
                  onChange={handleInputChange}
                  isInvalid={!!errors.official_email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.official_email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Gender")}</Form.Label>
                <Form.Select name="gender" value={formData.gender} onChange={handleInputChange}>
                  <option value="">Select Gender</option>
                  <option value="MALE">{t("Male")}</option>
                  <option value="FEMALE">{t("Female")}</option>
                  <option value="OTHER">{t("Other")}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Phone Number")} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="tel"
                  name="number"
                  value={formData.number}
                  onChange={handleInputChange}
                  isInvalid={!!errors.number}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.number}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Alternate Phone Number")}</Form.Label>
                <Form.Control
                  type="tel"
                  name="alternate_number"
                  value={formData.alternate_number}
                  onChange={handleInputChange}
                  isInvalid={!!errors.alternate_number}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.alternate_number}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Date of Birth")}</Form.Label>
                <Form.Control
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Guardian Name")}</Form.Label>
                <Form.Control
                  type="text"
                  name="guardian_name"
                  value={formData.guardian_name}
                  onChange={handleInputChange}
                  isInvalid={!!errors.guardian_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.guardian_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Marital Status")}</Form.Label>
                <Form.Select name="marital_status" value={formData.marital_status} onChange={handleInputChange}>
                  <option value="single">{t("Single")}</option>
                  <option value="married">{t("Married")}</option>
                  <option value="divorced">{t("Divorced")}</option>
                  <option value="widowed">{t("Widowed")}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Nationality")}</Form.Label>
                <Form.Select name="nationality" value={formData.nationality} onChange={handleInputChange}>
                  <option value="Indian">{t("Indian")}</option>
                </Form.Select>
              </Form.Group>
            </Col>

          </Row>

          <div className="mb-4">
            <h5 className="text-success">
              <i className="fa fa-briefcase me-2"></i>
              {t("Employment Details")}
            </h5>
            <hr />
          </div>

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Employment ID")} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="employment_id"
                  value={formData.employment_id}
                  onChange={handleInputChange}
                  isInvalid={!!errors.employment_id}
                  placeholder={t("Employment ID will be auto-generated or enter manually")}
                  className="mb-0"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.employment_id}
                </Form.Control.Feedback>
                <Form.Text className="text-muted small">
                  {t("Auto-generated when department & joining date are selected, but can be edited")}
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <div className="input-block mb-3 position-relative">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="col-form-label mb-0">
                    {t("Salary")} <span className="text-danger">*</span>
                  </label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="salaryType"
                        id="monthlyRadio"
                        value="monthly"
                        checked={salaryType === "monthly"}
                        onChange={(e) => handleSalaryTypeChange(e.target.value)}
                        style={{
                          transform: "scale(0.8)",
                          marginRight: "4px",
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="monthlyRadio"
                        style={{ fontSize: "14px" }}
                      >
                        {t("Per Month")}
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="salaryType"
                        id="annuallyRadio"
                        value="annually"
                        checked={salaryType === "annually"}
                        onChange={(e) => handleSalaryTypeChange(e.target.value)}
                        style={{
                          transform: "scale(0.8)",
                          marginRight: "4px",
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="annuallyRadio"
                        style={{ fontSize: "14px" }}
                      >
                        {t("Annually")}
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ position: "relative" }}>
                  <input
                    className={`form-control ${errors.salary ? 'is-invalid' : ''}`}
                    type={showSalary ? "text" : "password"}
                    value={formData?.salary}
                    onChange={handleInputChange}
                    style={{
                      paddingRight: "2.5rem",
                    }}
                    name="salary"
                    autoComplete="new-password"
                    placeholder={
                      salaryType === "monthly"
                        ? t("Enter monthly salary")
                        : t("Enter annual salary")
                    }
                  />
                  <span
                    className="position-absolute top-50 translate-middle-y"
                    style={{
                      right: "10px",
                      cursor: "pointer",
                      zIndex: "10",
                    }}
                    onClick={() => setShowSalary(!showSalary)}
                  >
                    {showSalary ? (
                      <i className="fa fa-eye-slash" style={{ fontSize: "16px" }} />
                    ) : (
                      <i className="fa fa-eye" style={{ fontSize: "16px" }} />
                    )}
                  </span>
                  {errors.salary && (
                    <div className="invalid-feedback d-block">
                      {errors.salary}
                    </div>
                  )}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Working Shift")} <span className="text-danger">*</span></Form.Label>
                <Form.Select name="working_shift" value={formData.working_shift} onChange={handleInputChange}>
                  {workingShifts.map(shift => (
                    <option key={shift.value} value={shift.value}>
                      {shift.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Select Reporter Department")} <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="reporter_department_id"
                  value={formData.reporter_department_id}
                  onChange={handleInputChange}
                  isInvalid={!!errors.reporter_department_id}
                >
                  <option value="">{t("Select Department")}</option>
                  {reporterDepartments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.department_name || dept.department}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.reporter_department_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Select Reporter")} <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="reporter_id"
                  value={formData.reporter_id}
                  onChange={handleInputChange}
                  isInvalid={!!errors.reporter_id}
                >
                  <option value="">{t("Select Reporter")}</option>
                  {employees.filter(emp => 
                    !formData.reporter_department_id || 
                    emp.department_id == formData.reporter_department_id
                  ).map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employment_id})
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.reporter_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Department")} <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleInputChange}
                  isInvalid={!!errors.department_id}
                >
                  <option value="">{t("Select Department")}</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.department_name || dept.department}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.department_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Designation")} <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  name="designation_id"
                  value={formData.designation_id}
                  onChange={handleInputChange}
                  isInvalid={!!errors.designation_id}
                  disabled={!formData.department_id}
                >
                  <option value="">{t("Select Designation")}</option>
                  {designations.map(desig => (
                    <option key={desig.id} value={desig.id}>
                      {desig.designation_name || desig.designation}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.designation_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <div className="input-block mb-3">
                <label className="col-form-label">
                  {t("Assign Role")} <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="role_id"
                  value={formData.role_id}
                  onChange={handleInputChange}
                >
                  <option value="">{t("Select Role")}</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
                {errors.role_id && (
                  <span className="text-danger">
                    {errors.role_id}
                  </span>
                )}
              </div>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Work Experience (in Years)")} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  name="work_experience"
                  value={formData.work_experience}
                  onChange={handleInputChange}
                  isInvalid={!!errors.work_experience}
                  min="0"
                  step="0.1"
                  placeholder="0.0"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.work_experience}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Joining Date")} <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  name="joining_date"
                  value={formData.joining_date}
                  onChange={handleInputChange}
                  isInvalid={!!errors.joining_date}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.joining_date}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Employment Type")}</Form.Label>
                <Form.Select name="employment_type" value={formData.employment_type} onChange={handleInputChange}>
                  <option value="full-time">{t("Full Time")}</option>
                  <option value="part-time">{t("Part Time")}</option>
                  <option value="intern">{t("Intern")}</option>
                  <option value="contractor">{t("Contractor")}</option>
                  <option value="hourly">{t("Contractor (Hourly)")}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {formData.employment_type === "hourly" && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("Hourly Rate")} *</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    name="hourly_rate"
                    value={formData.hourly_rate}
                    onChange={handleInputChange}
                    isInvalid={!!errors.hourly_rate}
                    placeholder={t("Enter rate per hour")}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.hourly_rate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Employment Status")}</Form.Label>
                <Form.Select name="employment_status" value={formData.employment_status} onChange={handleInputChange}>
                  <option value="probationary">{t("Probationary")}</option>
                  <option value="permanent">{t("Permanent")}</option>
                  <option value="intern">{t("Intern")}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("Probation End Date")}</Form.Label>
                <Form.Control
                  type="date"
                  name="probation_end_date"
                  value={formData.probation_end_date}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <div className="input-block mb-3">
                <label className="col-form-label">
                  Work mode <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  placeholder="Select"
                  name="selectedWorkLocation"
                  value={selectedWorkLocation ? selectedWorkLocation : formData?.work_mode}
                  onChange={(e) => {
                    selectOption(e, setSelectedWorkLocation);
                    handleInputChange({
                      target: {
                        name: "work_mode",
                        value: e.target.value
                      }
                    });
                  }}
                >
                  <option value="">Select Mode</option>
                  {workLocationTypes.map((item) => (
                    <option value={item.id} key={item.id}>
                      {item?.name}
                    </option>
                  ))}
                </select>
                {errors.work_mode && (
                  <span className="text-danger">
                    {errors.work_mode}
                  </span>
                )}
                {isHybridSelected(selectedWorkLocation) && (
                  <div className="mt-3">
                    <label className="col-form-label fw-semibold mb-2" style={{ fontSize: '0.85rem' }}>
                      Select Hybrid Schedule <span className="text-danger">*</span>
                      <small className="text-muted fw-normal ms-1">(choose WFH or Onsite for each day)</small>
                    </label>
                    <div className="border rounded-2 p-2" style={{ background: '#f8f9fa' }}>
                      <div className="d-flex flex-column gap-1">
                        {WEEKDAYS.map((day) => (
                          <div
                            key={day.value}
                            className="d-flex align-items-center gap-2 rounded-2 px-2 py-1"
                            style={{ width: 'fit-content', background: hybridDays[day.value] ? '#fff' : 'transparent', border: hybridDays[day.value] ? '1px solid #dee2e6' : '1px solid transparent' }}
                          >
                            <span style={{ minWidth: '32px', fontSize: '0.8rem', fontWeight: 600, color: '#495057' }}>{day.label}</span>
                            <button
                              type="button"
                              onClick={() => handleHybridDayType(day.value, 2)}
                              className="btn btn-sm py-0 px-2"
                              style={{
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderRadius: '4px',
                                background: hybridDays[day.value] === 2 ? '#0d6efd' : '#e9ecef',
                                color: hybridDays[day.value] === 2 ? '#fff' : '#6c757d',
                                border: 'none',
                                lineHeight: '1.8',
                              }}
                            >
                              WFH
                            </button>
                            <button
                              type="button"
                              onClick={() => handleHybridDayType(day.value, 1)}
                              className="btn btn-sm py-0 px-2"
                              style={{
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderRadius: '4px',
                                background: hybridDays[day.value] === 1 ? '#198754' : '#e9ecef',
                                color: hybridDays[day.value] === 1 ? '#fff' : '#6c757d',
                                border: 'none',
                                lineHeight: '1.8',
                              }}
                            >
                              Onsite
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    {Object.keys(hybridDays).length === 0 && (
                      <span className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>
                        * Select at least one day with WFH or Onsite
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            {t("Cancel")}
          </Button>

          <Button variant="success" type="submit" disabled={submitting}>
            {submitting ? (
              <i className="fa fa-spinner fa-spin me-2" />
            ) : (
              <i className="fa fa-user-plus me-2" />
            )}
            {t("Hire as Employee")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default HireCandidateModal;